import { useEffect, useRef, useState } from "react";
import { SliderProps } from "../../types/slider";

export const Slider: React.FC<SliderProps> = ({
  value,
  onChange,
  min,
  max,
  label,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const calculateValue = (clientX: number): number => {
    if (!sliderRef.current) return value;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newValue = Math.round(min + percentage * (max - min));

    return Math.max(min, Math.min(max, newValue));
  };

  const handleMove = (clientX: number) => {
    const newValue = calculateValue(clientX);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        e.preventDefault();
        handleMove(e.clientX);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging && e.touches[0]) {
        e.preventDefault();
        handleMove(e.touches[0].clientX);
      }
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleEnd);
      document.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, onChange, value, min, max]);

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <>
      <div className="sliderHeadingTxt">
        <label>{label}</label>
        <span>[{value}]</span>
      </div>
      <div
        ref={sliderRef}
        className="slideChanel"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsDragging(true);
          handleMove(e.clientX);
        }}
        onTouchStart={(e) => {
          setIsDragging(true);
          handleMove(e.touches[0].clientX);
        }}
        onClick={(e) => {
          if (!isDragging) {
            handleMove(e.clientX);
          }
        }}
      >
        <div className="slideBar">
          <div
            className="absolute h-full bg-[#18f7b1] rounded"
            style={{ width: `${percentage}%` }}
          />

          {Array.from({ length: max - min + 1 }, (_, i) => {
            const tickValue = i + min;
            const tickPercentage = ((tickValue - min) / (max - min)) * 100;
            return (
              <div
                key={tickValue}
                className="slidePoint"
                style={{ left: `${tickPercentage}%` }}
              />
            );
          })}
        </div>
        <div
          className="slideCircle"
          style={{ left: `calc(${percentage}% - 10px)` }}
        />
      </div>
    </>
  );
};